
import {
  computed,
  defineComponent,
  PropType,
  reactive,
  onUnmounted,
} from 'vue';

import { UserExam, UserExamList, QuestionBaseInfoList } from '@/types';
import { useService } from '@kaokei/use-vue-service';
import { UserExamService } from '@/services/userExam.service';
import { useRouter, useRoute } from 'vue-router';
import { DaoService } from '@/services/dao.service';

import {
  USER_EXAM_PASSED,
  USER_EXAM_AWARDED,
  QUESTION_TYPE,
  value2label,
  formatDateToSecond,
  preloadIDE,
} from '@/utils';

import ExamInfoModal from '@/components/ExamInfoModal.vue';
import { environment } from '@/utils/environment';

export default defineComponent({
  name: 'ContestTable',
  components: {
    ExamInfoModal,
  },
  props: {
    list: {
      type: Array as PropType<UserExamList>,
    },
  },
  setup() {
    const route = useRoute();
    const isNocFlag = computed(() => route.query.platform === 'noc');

    const columns = computed(() => {
      const firstColumn = {
        title: '活动ID',
        dataIndex: 'contestInfoId',
        key: 'contestInfoId',
        fixed: 'left',
      };
      const cols = [
        {
          title: isNocFlag.value ? '比赛信息' : '活动信息',
          key: 'contestInfo',
          slots: { customRender: 'contestInfo' },
          width: 200,
          fixed: 'left',
        },
        {
          title: isNocFlag.value ? '比赛开始时间' : '考试开始时间',
          key: 'startTime',
          slots: { customRender: 'startTime' },
          width: 160,
        },
        {
          title: isNocFlag.value ? '比赛结束时间' : '考试结束时间',
          key: 'endTime',
          slots: { customRender: 'endTime' },
          width: 160,
        },
        {
          title: '组别',
          dataIndex: 'contestScheduledName',
          key: 'contestScheduledName',
          width: 200,
        },
        {
          title: isNocFlag.value ? '比赛阶段' : '活动阶段',
          dataIndex: 'contestStageName',
          key: 'contestStageName',
          width: 100,
        },
        {
          title: '得分',
          key: 'totalScore',
          slots: { customRender: 'totalScore' },
          width: 80,
        },
        {
          title: '晋级结果',
          key: 'passed',
          slots: { customRender: 'passed' },
          width: 80,
        },
        {
          title: '奖项',
          key: 'awarded',
          slots: { customRender: 'awarded' },
          width: 80,
        },
        {
          title: '操作',
          key: 'operation',
          fixed: 'right',
          slots: { customRender: 'operation' },
        },
      ];
      return isNocFlag.value ? cols : [firstColumn, ...cols];
    });

    const router = useRouter();
    const userExamService = useService(UserExamService);
    const daoService = useService(DaoService);

    const getBaseInfoText = (info: QuestionBaseInfoList) => {
      return info
        .map(
          info =>
            `${info.count}道${value2label(info.questionTypeId, QUESTION_TYPE)}`
        )
        .join('、');
    };

    const checkDetail = (item: UserExam) => {
      router.push({
        name: 'UserItemDetail',
        params: {
          id: item.userExamId,
        },
        query: route.query,
      });
    };

    const examInfo = reactive({
      userExamId: 0,
      visible: false,
      baseInfoText: '',
      answerRule: '',
      contestId: 0,
    });
    const showExamInfo = async (item: UserExam) => {
      const showAction = () => {
        daoService.getExamInfo(item.userExamId).then(info => {
          examInfo.baseInfoText = getBaseInfoText(info.questionInfoList);
          examInfo.answerRule = info.answerRule;
          preloadIDE(); // 预加载ide
          examInfo.userExamId = item.userExamId;
          examInfo.visible = true;
          examInfo.contestId = item.contestInfoId;
        });
      };
      if (item.examType === 1) {
        // if (item.userExamId) {
        daoService.preStartMockExam(item.userExamId).then(() => {
          showAction();
        });
      } else {
        showAction();
      }
    };

    const onPaginationChange = (pa: any) => {
      userExamService.setPageNum(pa.current);
      userExamService.setPageSize(pa.pageSize);
      userExamService.search();
    };

    const pagination = computed(() => ({
      showSizeChanger: true,
      total: userExamService.total,
      current: userExamService.pageNum,
      pageSize: userExamService.pageSize,
    }));

    const goToItemDetail = (item: UserExam) => {
      if (isNocFlag.value) return;
      router.push({
        name: 'ItemDetail',
        params: {
          id: item.contestInfoId,
        },
        query: route.query,
      });
    };

    const goToExamAnswer = (item: UserExam) => {
      router.push({
        name: 'Examination',
        params: {
          id: item.userExamId,
          answerId: item.answerPaperId,
        },
        query: route.query,
      });
    };

    const getBtnText = (item: UserExam) => {
      const hideGetCertification =
        environment === 'production'
          ? item.contestInfoId === 797
          : item.contestInfoId === 36;
      if (item.examType === 1) {
        return '去模拟';
      }
      if (isNocFlag.value) {
        return '去比赛';
      }
      return item.activityId === 259
        ? '去比赛'
        : hideGetCertification
        ? '参与活动'
        : '去考试';
    };

    onUnmounted(() => {
      userExamService.clearTimer();
    });

    const getRowClassName = (record: any) => {
      if (record.status === 1) {
        return 'row-ing';
      }
      if (record.status === 0 && record.endTime < Date.now()) {
        return 'row-ended';
      }
      return '';
    };

    return {
      examInfo,
      columns,
      userExamService,
      pagination,
      USER_EXAM_PASSED,
      USER_EXAM_AWARDED,
      value2label,
      checkDetail,
      showExamInfo,
      onPaginationChange,
      formatDateToSecond,
      goToItemDetail,
      goToExamAnswer,
      getBtnText,
      getRowClassName,
    };
  },
});
